/**
 * Dashboard hooks for all user roles
 */

import { useQuery } from '@tanstack/react-query';
import { 
  patientsApi, 
  doctorsApi, 
  coordinatorsApi, 
  therapistsApi, 
  nursesApi 
} from '../api';

/**
 * Patient Dashboard Hook (for admins/doctors viewing patient data)
 */
export function usePatientDashboard(patientId: number, enabled = true) {
  return useQuery({
    queryKey: ['patient-dashboard', patientId],
    queryFn: () => patientsApi.getPatientDashboardSummary(patientId),
    staleTime: 2 * 60 * 1000, // 2 minutes
    refetchInterval: 5 * 60 * 1000, // Refetch every 5 minutes
    enabled: enabled && !!patientId,
  });
}

/**
 * My Dashboard Hook (for patient role viewing their own dashboard)
 */
export function useMyDashboard(enabled = true) {
  return useQuery({
    queryKey: ['my-dashboard'],
    queryFn: () => patientsApi.getMyDashboard(),
    staleTime: 2 * 60 * 1000, // 2 minutes
    refetchInterval: 5 * 60 * 1000, // Refetch every 5 minutes
    enabled,
  });
}

/**
 * Doctor Dashboard Hook
 */
export function useDoctorDashboard(enabled = true) {
  return useQuery({
    queryKey: ['doctor-dashboard'],
    queryFn: () => doctorsApi.getDoctorDashboardSummary(),
    staleTime: 3 * 60 * 1000, // 3 minutes
    refetchInterval: 5 * 60 * 1000, // Refetch every 5 minutes
    enabled,
  });
}

/**
 * Coordinator Dashboard Hook
 */
export function useCoordinatorDashboard(enabled = true) {
  return useQuery({
    queryKey: ['coordinator-dashboard'],
    queryFn: () => coordinatorsApi.getCoordinatorDashboardSummary(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchInterval: 10 * 60 * 1000, // Refetch every 10 minutes
    enabled,
  });
}

/**
 * Therapist Dashboard Hook
 */
export function useTherapistDashboard(enabled = true) {
  return useQuery({
    queryKey: ['therapist-dashboard'],
    queryFn: () => therapistsApi.getTherapistDashboardSummary(),
    staleTime: 3 * 60 * 1000, // 3 minutes
    refetchInterval: 5 * 60 * 1000, // Refetch every 5 minutes
    enabled,
  });
}

/**
 * Nurse Dashboard Hook
 */
export function useNurseDashboard(enabled = true) {
  return useQuery({
    queryKey: ['nurse-dashboard'],
    queryFn: () => nursesApi.getNurseDashboardSummary(),
    staleTime: 2 * 60 * 1000, // 2 minutes
    refetchInterval: 5 * 60 * 1000, // Refetch every 5 minutes
    enabled,
  });
}
